import { useEffect, type ReactNode } from "react";

import { usePageChrome } from "@/shared/layout/PageChromeContext";
import { useIsMobile } from "@/shared/hooks/useIsMobile";

/**
 * Puts a page's title and description in the desktop TOP BAR, and clears the
 * in-page hero.
 *
 * Every primary screen used to render its name twice: once in the 64px bar
 * (from the active nav item) and again beneath it as a 24-28px heading with a
 * subtitle. That second copy cost ~100 vertical pixels on every page to repeat
 * words the seller had just read one line above. The bar was holding a single
 * 15px line and has room for both, so the description moves up and the heading
 * goes away — the pattern the restaurant dashboard already uses.
 *
 * Mobile is untouched: there is no top bar there, and each screen keeps its own
 * `MobileScreenHeader`.
 */
export function usePageTitleChrome({
  icon,
  title,
  subtitle,
  heading = false,
}: {
  /** The page's nav glyph, so the bar reads the same as the sidebar row. */
  icon?: ReactNode;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Render the title as the page's `<h1>` (desktop has no other heading on
   *  the list screens — Orders, Products, Customers; roundtable 2, C8). Off
   *  for pages that already carry their own `<h1>` below the bar. */
  heading?: boolean;
}): void {
  const isMobile = useIsMobile();
  const { setHero, setBreadcrumb } = usePageChrome();

  useEffect(() => {
    setHero(null);
    return () => setHero(null);
  }, [setHero]);

  useEffect(() => {
    if (isMobile) {
      setBreadcrumb(null);
      return () => setBreadcrumb(null);
    }
    setBreadcrumb(
      <div className="flex min-w-0 items-center gap-2.5">
        {icon ? (
          <span
            aria-hidden="true"
            className="inline-flex shrink-0 [&_svg]:h-[19px] [&_svg]:w-[19px]"
          >
            {icon}
          </span>
        ) : null}
        <span className="flex min-w-0 flex-col leading-tight">
          {heading ? (
            <h1 className="m-0 truncate text-[15px] font-semibold text-[color:var(--text-primary)]">
              {title}
            </h1>
          ) : (
            <span className="truncate text-[15px] font-semibold text-[color:var(--text-primary)]">
              {title}
            </span>
          )}
          {subtitle ? (
            <span className="truncate text-[12px] font-normal text-[color:var(--text-muted)]">
              {subtitle}
            </span>
          ) : null}
        </span>
      </div>,
    );
    return () => setBreadcrumb(null);
  }, [setBreadcrumb, isMobile, icon, title, subtitle, heading]);
}
